import React, { createContext, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { addDays, format } from 'date-fns';
import { captureException } from '@sentry/react';
import { useBusinessTimezone, useClientUser } from '@/hooks';
import { asUTCDate } from '@/utilities/convertToISO';
import { useQuery } from '@apollo/client';
import { LOADBOARD_QUERY, QUERY_PLANNING_DATA } from './graphql/queries';
import { ORDER_FILTER_OPTIONS } from './constants';
import searchOrders from '@/utilities/searchOrders';
import { isEmpty } from 'lodash';
import { PLAN_PAGE_PAST_SCHEDULED, PLAN_PAGE_UNSCHEDULED } from '@/constants/featureFlags';
import { deriveOrderRows, deriveShellRoutes, isOrderAssigned, isResourceScheduled } from './planningDerivations';
import { ROUTE_FILTER_ALL, getRouteFilterOptions, orderMatchesRoute } from './util/routeFilter';
import { getShipperFilterOptions } from './util/shippers';
import { resourcesAwaitingRoute } from './util/ordersOffResource';

const CATEGORY_KEYS = [
    'ORDER_TYPE',
    'WAREHOUSING_TYPE',
    'SCHEDULING_TYPE',
    'MARKETPLACE_TYPE',
    'TAG_TYPE',
    'SHIPPER_TYPE',
];

// Legacy monolithic context: composes the granular contexts below into the
// original provider value shape. New consumers should read the granular
// contexts; legacy consumers migrate off this file-by-file.
export const PlanningContext = createContext();
export const PlanningDataContext = createContext();
export const PlanningFilterContext = createContext();
export const PlanningSelectionContext = createContext();
export const PlanningUiContext = createContext();
export const PlanningNotificationContext = createContext();

const PlanningContextProvider = ({ children }) => {
    const { user_id, roles, circles, test_acc, resources, shipping_partners, preferences_next_day_returns } =
        useClientUser();
    const tz = useBusinessTimezone();
    const [deliveryDate, setDeliveryDate] = useState(
        window.sessionStorage.getItem('plan_page_del_date') || format(new Date(), 'yyyy-MM-dd')
    );
    const [loadDeliveryDate, setLoadDeliveryDate] = useState(
        window.sessionStorage.getItem('plan_page_del_date') || format(new Date(), 'yyyy-MM-dd')
    );
    const updateDeliveryDate = useCallback((date) => {
        setSelectedRoute(null);
        setSelectedOrders({});
        setSelectedLoads({});
        setRouteFilter(ROUTE_FILTER_ALL);
        setDeliveryDate(date);
        setLoadDeliveryDate(date);
        window?.sessionStorage?.setItem('plan_page_del_date', date);
    }, []);
    // Misc state
    const [showAllDirections, setShowAllDirections] = useState(false);
    const [showLoadBoardLoads, setShowLoadBoardLoads] = useState(false);
    const [showNetworkRoutes, setShowNetworkRoutes] = useState(false);
    const [hideRoutedOrders, setHideRoutedOrders] = useState(false);
    const toggleShowAllDirections = useCallback(() => setShowAllDirections((prev) => !prev), []);
    const toggleShowLoadBoardLoads = useCallback(() => setShowLoadBoardLoads((prev) => !prev), []);
    const toggleShowNetworkRoutes = useCallback(() => setShowNetworkRoutes((prev) => !prev), []);
    const toggleHideRoutedOrders = useCallback(() => setHideRoutedOrders((prev) => !prev), []);
    const [orderCategories, setOrderCategories] = useState({
        ORDER_TYPE: { ALL: true },
        WAREHOUSING_TYPE: circles?.['plan-page-received'] ? { RECEIVED: true } : { ALL: true },
        SCHEDULING_TYPE:
            circles?.[PLAN_PAGE_UNSCHEDULED] || circles?.[PLAN_PAGE_PAST_SCHEDULED]
                ? { ALL: true }
                : { SCHEDULED: true },
        MARKETPLACE_TYPE: { ALL: true },
        TAG_TYPE: { ALL: true },
    });
    // `optionKeys` is the category's options. Static ones are on ORDER_FILTER_OPTIONS, but
    // SHIPPER_TYPE's come from the board, so the caller passes them.
    const toggleOrderCategory = useCallback(
        (category, option, optionKeys) =>
            setOrderCategories((prev) => {
                // Selecting ALL always collapses the category to just `{ ALL: true }`.
                if (option === 'ALL') {
                    return { ...prev, [category]: { ALL: true } };
                }

                // Multi-select behavior for non-ALL options: start from the current
                // set of selected non-ALL keys, toggle the clicked one, then apply
                // two invariants:
                //   1. If no options remain selected, snap to { ALL: true }.
                //   2. If every non-ALL option in ORDER_FILTER_OPTIONS[category] is
                //      now selected, collapse to { ALL: true } — "all of them" is
                //      identical to "all" as far as the filter is concerned.
                //      Skip when there is only one non-ALL option (e.g. Tags → VIP):
                //      selecting that single facet is a real filter, not "all".
                const currentSelected = Object.entries(prev[category] || {})
                    .filter(([key, value]) => value && key !== 'ALL')
                    .map(([key]) => key);
                const isCurrentlySelected = currentSelected.includes(option);
                const nextSelected = isCurrentlySelected
                    ? currentSelected.filter((k) => k !== option)
                    : [...currentSelected, option];

                if (nextSelected.length === 0) {
                    return { ...prev, [category]: { ALL: true } };
                }

                const allNonAllKeys = (optionKeys || Object.keys(ORDER_FILTER_OPTIONS[category] || {})).filter(
                    (k) => k !== 'ALL'
                );
                if (
                    allNonAllKeys.length > 1 &&
                    nextSelected.length === allNonAllKeys.length &&
                    allNonAllKeys.every((k) => nextSelected.includes(k))
                ) {
                    return { ...prev, [category]: { ALL: true } };
                }

                return {
                    ...prev,
                    [category]: Object.fromEntries(nextSelected.map((k) => [k, true])),
                };
            }),
        []
    );
    const [clashingRoutes, setClashingRoutes] = useState(null);
    const [clashingDriver, setClashingDriver] = useState(null);
    const [bid, setBid] = useState(null);

    const [selectedDrivers, setSelectedDrivers] = useState({});
    const [selectedRoute, setSelectedRoute] = useState(null);
    const [selectedOrders, setSelectedOrders] = useState({});
    const [expandedOrders, setExpandedOrders] = useState({});
    const [selectedLoads, setSelectedLoads] = useState({});
    const toggleSelectOrder = useCallback((order_id) => {
        setSelectedOrders((prev) => ({
            ...prev,
            [order_id]: !prev[order_id],
        }));
    }, []);
    const toggleSelectLoad = useCallback((order_id) => {
        setSelectedLoads((prev) => ({
            ...prev,
            [order_id]: !prev[order_id],
        }));
    }, []);
    const selectMultiOrders = useCallback((orderIds, selected = true) => {
        if (!orderIds) return;
        setSelectedOrders((prev) => ({
            ...prev,
            ...Object.fromEntries(orderIds.map((orderId) => [orderId, selected])),
        }));
        setSelectedLoads({});
    }, []);
    const selectMultiLoads = useCallback((orderIds, selected = true) => {
        if (!orderIds) return;
        // Replaces (not merges) the current load selection.
        setSelectedLoads(Object.fromEntries(orderIds.map((orderId) => [orderId, selected])));
        setSelectedOrders({});
    }, []);

    const [notification, setNotification] = useState(null);
    const setError = useCallback((error, userMsg) => {
        console.error(error);
        captureException(error);
        setNotification({
            severity: 'error',
            message: userMsg || 'Error processing request',
        });
    }, []);

    // Query variables are scoped to ownership + a fixed scheduling window
    // (always the single day, plus unscheduled orders if the user has the
    // plan-page-unscheduled circle). Category filtering (order type,
    // warehousing, marketplace) happens client-side via `filteredOrders`
    // below so the same filter pipeline also applies to routed orders that
    // come from the `routes` query.
    // Mirrored client-side by graphql/planningBuckets.js — changing this
    // shape requires updating that module.
    const [variables, loadBoardVariables] = useMemo(() => {
        const startCutoff = asUTCDate(deliveryDate);
        const endCutoff = addDays(startCutoff, 1);
        // The "past scheduled" flag widens the lower bound of the date window back
        // 30 days so scheduled-but-unrouted "missed" orders are pulled in. It is
        // independent of the unscheduled bucket: only the plan-page-unscheduled
        // circle controls whether null-date orders are included.
        const pastScheduled = !!circles?.[PLAN_PAGE_PAST_SCHEDULED];
        const includeUnscheduled = !!circles?.[PLAN_PAGE_UNSCHEDULED];
        const windowStart = pastScheduled ? addDays(startCutoff, -30) : startCutoff;

        const datePredicate = (attr) => ({
            _or: [
                { _and: [{ [attr]: { _gte: windowStart } }, { [attr]: { _lt: endCutoff } }] },
                ...(includeUnscheduled ? [{ [attr]: { _is_null: true } }] : []),
            ],
        });

        return [
            {
                orderCondition: {
                    _or: [
                        { shipper_id: { _eq: user_id } },
                        ...(roles?.['CARRIER'] ? [{ carrier_id: { _eq: user_id } }] : []),
                        ...(roles?.['WAREHOUSE_CARRIER'] ? [{ warehouse_carrier_id: { _eq: user_id } }] : []),
                    ],
                },
                dropoffCondition: datePredicate('delivery_date'),
                pickupCondition: datePredicate('pickup_date'),
                from_date: startCutoff,
                to_date: endCutoff,
                user_id,
            },
            {
                user_id,
                end: endCutoff,
                test_acc,
                currentTime: new Date().toISOString(),
            },
        ];
    }, [deliveryDate, user_id, roles, circles]);

    const { loading, data, refetch, subscribeToMore, networkStatus } = useQuery(QUERY_PLANNING_DATA, {
        variables,
        fetchPolicy: 'cache-and-network',
        onError: (error) => {
            setError(error, `Error getting orders and routes for delivery date ${deliveryDate}`);
        },
    });

    // Row identity cache: unchanged rows keep the same object identity across
    // recomputes (Apollo broadcasts) so downstream memoization can bail out.
    const rowCacheRef = useRef(new Map());
    const [orders, routes] = useMemo(() => {
        const {
            rows,
            routes: serverRoutes,
            rowCache,
        } = deriveOrderRows({
            data,
            deliveryDate,
            prevRowCache: rowCacheRef.current,
        });
        rowCacheRef.current = rowCache;
        return [rows, serverRoutes];
    }, [data, deliveryDate]);

    // Unified client-side filter pipeline: the multi-select category
    // filters (order type, warehousing, scheduling, marketplace, tags),
    // the Assignment filter, and Search all run against the full
    // combined `orders` array (routed + unassigned). Both PlanningOrders and
    // PlanningMap consume the result so the list and map markers stay in
    // lockstep under every filter.
    const [searchText, setSearchText] = useState('');
    const [assignmentFilter, setAssignmentFilter] = useState('ALL');
    const [routeFilter, setRouteFilter] = useState(ROUTE_FILTER_ALL);

    // Built from the server routes, not withGeneratedRoutes: shell routes hold no
    // orders, so they'd never match and would only pad the dropdown.
    const routeFilterOptions = useMemo(() => getRouteFilterOptions(routes), [routes]);

    // A selected route can vanish — the plan date changed, the route was deleted,
    // or its last order was unassigned. Derive the active value instead of holding
    // the stale id, otherwise the list silently empties.
    const activeRouteFilter = routeFilterOptions.some((o) => o.value === routeFilter) ? routeFilter : ROUTE_FILTER_ALL;

    // Shipper options come from the board rather than a fixed list, but in the same shape, so
    // the filter pipeline and the dropdowns treat them like any other category.
    const filterOptionsByCategory = useMemo(
        () => ({
            ...ORDER_FILTER_OPTIONS,
            SHIPPER_TYPE: getShipperFilterOptions({ orders, shipping_partners, user_id }),
        }),
        [orders, shipping_partners, user_id]
    );

    const filteredOrders = useMemo(() => {
        let result = orders;

        for (const category of CATEGORY_KEYS) {
            const selected = orderCategories?.[category] || {};
            if (selected.ALL) continue;
            const predicates = Object.entries(selected)
                .filter(([key, value]) => value && key !== 'ALL')
                .map(([key]) => filterOptionsByCategory[category]?.[key]?.predicate)
                .filter(Boolean);
            if (predicates.length === 0) continue;
            // OR within a category (any selected option matches).
            result = result.filter((o) => predicates.some((p) => p(o)));
        }

        if (assignmentFilter === 'ASSIGNED') {
            result = result.filter(isOrderAssigned);
        } else if (assignmentFilter === 'UNASSIGNED') {
            result = result.filter((o) => !isOrderAssigned(o));
        }

        if (activeRouteFilter !== ROUTE_FILTER_ALL) {
            result = result.filter((o) => orderMatchesRoute(o, activeRouteFilter));
        }

        if (!isEmpty(searchText)) {
            result = searchOrders(searchText, { all: result }).all;
        }

        return result;
    }, [orders, orderCategories, filterOptionsByCategory, assignmentFilter, activeRouteFilter, searchText]);

    const {
        loading: loadBoardLoading,
        data: loadBoardData,
        refetch: loadBoardRefetch,
    } = useQuery(LOADBOARD_QUERY, {
        skip: !circles?.['plan-page-load-board'] || !showLoadBoardLoads,
        variables: loadBoardVariables,
        fetchPolicy: 'network-only',
        onError: (error) => {
            setError(error, `Error getting marketplace loads for ${deliveryDate}`);
        },
    });
    const loadBoardLoads = useMemo(() => {
        if (loadBoardData?.orders) {
            return loadBoardData.orders;
        }
        return [];
    }, [loadBoardData]);
    const selectedLoadBoardLoads = useMemo(() => {
        return (loadBoardLoads || []).filter((order) => selectedLoads[order.order_id]);
    }, [loadBoardLoads, selectedLoads]);

    const [modalOpen, setModalOpen] = useState(null);
    const [actionableRoute, setActionableRoute] = useState(null);
    const [actionableStop, setActionableStop] = useState(null);
    const [actionableOrder, setActionableOrder] = useState(null);
    const [routesToSubmitInternal, setRoutesToSubmitInternal] = useState([]);
    const [lockAllRoutesClicked, setLockAllRoutesClicked] = useState(false);

    // Shell route id/object caches (see planningDerivations.deriveShellRoutes).
    const shellIdsRef = useRef(new Map());
    const shellCacheRef = useRef(new Map());
    const prevDeliveryDateRef = useRef(deliveryDate);

    // A shell uuid must never span two plan dates: an assign on day A
    // consumes it, and day B's query can't return day-A routes for the
    // per-resource eviction to catch. Reset synchronously during render — the
    // withGeneratedRoutes memo below mints shells in this same render pass,
    // so an effect-based clear would run one render too late and let day B
    // reuse day-A uuids.
    if (prevDeliveryDateRef.current !== deliveryDate) {
        shellIdsRef.current.clear();
        shellCacheRef.current.clear();
        prevDeliveryDateRef.current = deliveryDate;
    }

    useEffect(() => {
        setSelectedOrders({});
        setSelectedLoads({});
        setSelectedRoute(null);
    }, [deliveryDate]);

    // Gated on having a routes result, not on `loading`: returning to a cached
    // date reads data with loading: true, and an identical network result
    // leaves `data` referentially unchanged — a loading gate would strand the
    // shells empty until some unrelated routes change.
    const routesLoaded = !!data?.routes;
    const availableResources = useMemo(() => {
        if (!deliveryDate || !resources || !routesLoaded) return [];

        return resources
            .filter((resource) => isResourceScheduled(resource, deliveryDate, tz))
            .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    }, [deliveryDate, resources, routesLoaded, tz]);

    // Joined to a string so the shell memo below only reruns when the set changes, not on every
    // order broadcast.
    const awaitingRouteKey = useMemo(
        () => resourcesAwaitingRoute({ orders, routes, deliveryDate }).sort().join(','),
        [orders, routes, deliveryDate]
    );

    const [withGeneratedRoutes, allRoutes] = useMemo(() => {
        const { shells, shellCache } = deriveShellRoutes({
            routes,
            availableResources,
            awaitingRoute: awaitingRouteKey ? awaitingRouteKey.split(',') : [],
            shellIds: shellIdsRef.current,
            prevShells: shellCacheRef.current,
            shipper_id: user_id,
            deliveryDate,
            preferences_next_day_returns,
            tz,
        });
        shellCacheRef.current = shellCache;
        const allRoutes = [...routes, ...shells];
        return [allRoutes.filter((r) => r.status !== 'deleted'), allRoutes];
    }, [routes, availableResources, awaitingRouteKey]);

    const callbacksValue = useMemo(
        () => ({
            refetch,
            subscribeToMore,
            loadBoardRefetch,
            setSelectedDrivers,
            setLockAllRoutesClicked,
            setRoutesToSubmitInternal,
        }),
        [refetch, subscribeToMore, loadBoardRefetch]
    );

    // filteredOrders and selectedLoadBoardLoads deliberately live in the
    // filter/selection contexts so keystrokes and selection don't tick data.
    const dataValue = useMemo(
        () => ({
            state: {
                orders,
                routes: withGeneratedRoutes,
                allRoutes,
                loading: loading || networkStatus === 4,
                variables,
                loadBoardLoads,
                loadBoardLoading,
                selectedDrivers,
                routesToSubmitInternal,
                lockAllRoutesClicked,
            },
            callbacks: callbacksValue,
            deliveryDate,
            updateDeliveryDate,
            loadDeliveryDate,
            setLoadDeliveryDate,
            circles,
        }),
        [
            orders,
            withGeneratedRoutes,
            allRoutes,
            loading,
            networkStatus,
            variables,
            loadBoardLoads,
            loadBoardLoading,
            selectedDrivers,
            routesToSubmitInternal,
            lockAllRoutesClicked,
            callbacksValue,
            deliveryDate,
            updateDeliveryDate,
            loadDeliveryDate,
            circles,
        ]
    );

    const filterValue = useMemo(
        () => ({
            filteredOrders,
            searchText,
            setSearchText,
            orderCategories,
            toggleOrderCategory,
            assignmentFilter,
            setAssignmentFilter,
            routeFilter: activeRouteFilter,
            setRouteFilter,
            routeFilterOptions,
            filterOptionsByCategory,
        }),
        [
            filteredOrders,
            searchText,
            orderCategories,
            assignmentFilter,
            activeRouteFilter,
            routeFilterOptions,
            filterOptionsByCategory,
            toggleOrderCategory,
        ]
    );

    const selectionValue = useMemo(
        () => ({
            selectedOrders,
            setSelectedOrders,
            toggleSelectOrder,
            selectMultiOrders,
            expandedOrders,
            setExpandedOrders,
            selectedRoute,
            setSelectedRoute,
            selectedLoads,
            setSelectedLoads,
            toggleSelectLoad,
            selectMultiLoads,
            selectedLoadBoardLoads,
        }),
        [
            selectedOrders,
            expandedOrders,
            selectedRoute,
            selectedLoads,
            selectedLoadBoardLoads,
            toggleSelectOrder,
            selectMultiOrders,
            toggleSelectLoad,
            selectMultiLoads,
        ]
    );

    const uiValue = useMemo(
        () => ({
            modalOpen,
            setModalOpen,
            actionableRoute,
            setActionableRoute,
            actionableStop,
            setActionableStop,
            actionableOrder,
            setActionableOrder,
            clashingRoutes,
            setClashingRoutes,
            clashingDriver,
            setClashingDriver,
            bid,
            setBid,
            showAllDirections,
            toggleShowAllDirections,
            showLoadBoardLoads,
            toggleShowLoadBoardLoads,
            showNetworkRoutes,
            toggleShowNetworkRoutes,
            hideRoutedOrders,
            toggleHideRoutedOrders,
            setError,
            setNotification,
        }),
        [
            modalOpen,
            actionableRoute,
            actionableStop,
            actionableOrder,
            clashingRoutes,
            clashingDriver,
            bid,
            showAllDirections,
            showLoadBoardLoads,
            showNetworkRoutes,
            hideRoutedOrders,
            toggleShowAllDirections,
            toggleShowLoadBoardLoads,
            toggleShowNetworkRoutes,
            toggleHideRoutedOrders,
            setError,
        ]
    );

    const notificationValue = useMemo(() => ({ notification, setNotification }), [notification]);

    // Exact legacy provider shape (minus the removed direction-progress keys)
    // composed from the granular values so unmigrated consumers see no change.
    const legacyValue = useMemo(() => {
        const { selectedLoadBoardLoads: _selectedLoadBoardLoads, ...selectionRest } = selectionValue;
        const { filteredOrders: _filteredOrders, ...filterRest } = filterValue;
        return {
            ...dataValue,
            state: {
                ...dataValue.state,
                filteredOrders: filterValue.filteredOrders,
                selectedLoadBoardLoads: selectionValue.selectedLoadBoardLoads,
            },
            ...selectionRest,
            ...uiValue,
            notification: notificationValue.notification,
            ...filterRest,
        };
    }, [dataValue, filterValue, selectionValue, uiValue, notificationValue]);

    return (
        <PlanningDataContext.Provider value={dataValue}>
            <PlanningFilterContext.Provider value={filterValue}>
                <PlanningSelectionContext.Provider value={selectionValue}>
                    <PlanningUiContext.Provider value={uiValue}>
                        <PlanningNotificationContext.Provider value={notificationValue}>
                            <PlanningContext.Provider value={legacyValue}>{children}</PlanningContext.Provider>
                        </PlanningNotificationContext.Provider>
                    </PlanningUiContext.Provider>
                </PlanningSelectionContext.Provider>
            </PlanningFilterContext.Provider>
        </PlanningDataContext.Provider>
    );
};

export const withPlanningContext = (Component) => (props) =>
    (
        <PlanningContextProvider>
            <Component {...props} />
        </PlanningContextProvider>
    );
